(() => {
  const ROOT_SELECTOR = '[data-hr-spirit-listicle-owned]';
  const PRODUCT_ANCHOR_ID = 'Shop Now';

  const byRid = (root, rid) => rid ? root.querySelector(`[data-rid="${rid}"]`) : null;

  const parseConfig = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-hr-spirit-listicle-config]')?.textContent || '');
    } catch (error) {
      console.error('High Rider: invalid Spirit listicle configuration.', error);
      return null;
    }
  };

  const bindKeyboardActivation = (element, callback) => {
    if (!element || element.matches('button, a[href], input, select, textarea')) return;
    element.tabIndex = element.tabIndex >= 0 ? element.tabIndex : 0;
    element.setAttribute('role', element.getAttribute('role') || 'button');
    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      callback(event);
    });
  };

  const initializeCarousel = (root, gallery = []) => {
    const carousel = root.querySelector('[data-replo-carousel="true"]');
    if (!carousel) return;

    const tracks = [...carousel.querySelectorAll('[data-replo-part="slide-track"]')];
    const mainTrack = tracks.find((track) => track.querySelectorAll('[data-replo-part="slide"]').length > 5) || tracks[0];
    const thumbnailTrack = tracks.find((track) => track !== mainTrack);
    if (!mainTrack) return;

    const uniqueSlides = (track) => {
      const seen = new Set();
      return [...track.querySelectorAll(':scope > [data-replo-part="slide"]')].filter((slide) => {
        const sourceIndex = slide.dataset.slideSourceIndex ?? slide.dataset.slideIndex;
        if (seen.has(sourceIndex)) {
          slide.remove();
          return false;
        }
        seen.add(sourceIndex);
        slide.removeAttribute('data-is-cloned');
        return true;
      });
    };

    const syncSlides = (track, slides, isThumbnailTrack) => {
      if (!track || !slides.length || !Array.isArray(gallery) || !gallery.length) return slides;

      const template = slides[slides.length - 1];
      const syncedSlides = gallery.map((media, index) => {
        const slide = slides[index] || template.cloneNode(true);
        const slideId = `hr-spirit-listicle-${isThumbnailTrack ? 'thumbnail' : 'slide'}-${index}`;
        slide.id = slideId;
        slide.dataset.slideId = slideId;
        slide.dataset.slideIndex = String(index);
        slide.dataset.slideSourceIndex = String(index);
        slide.dataset.isActive = String(index === 0);
        slide.removeAttribute('data-is-cloned');
        slide.removeAttribute('data-is-target');
        slide.setAttribute('aria-label', `Slide ${index + 1}`);

        if (isThumbnailTrack) {
          slide.removeAttribute('aria-hidden');
          slide.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          slide.tabIndex = 0;
        } else {
          slide.setAttribute('aria-hidden', String(index !== 0));
          slide.tabIndex = index === 0 ? 0 : -1;
        }

        if (media?.url) {
          slide.querySelectorAll('source').forEach((source) => { source.srcset = media.url; });
          slide.querySelectorAll('img').forEach((image) => {
            image.src = media.url;
            image.removeAttribute('srcset');
            if (typeof media.alt === 'string') image.alt = media.alt;
          });
        }
        return slide;
      });

      track.replaceChildren(...syncedSlides);
      return syncedSlides;
    };

    const mainSlides = syncSlides(mainTrack, uniqueSlides(mainTrack), false);
    const thumbnailSlides = thumbnailTrack ? syncSlides(thumbnailTrack, uniqueSlides(thumbnailTrack), true) : [];
    if (!mainSlides.length) return;

    const previous = carousel.querySelector('[data-direction="previous"]');
    const next = carousel.querySelector('[data-direction="next"]');
    const status = carousel.querySelector('[aria-live="polite"]');
    let activeIndex = 0;
    let touchStartX = null;

    const setActive = (requestedIndex, animate = true) => {
      activeIndex = (requestedIndex + mainSlides.length) % mainSlides.length;
      mainTrack.style.setProperty('transition', animate ? 'transform 320ms ease' : 'none', 'important');
      mainTrack.style.setProperty('transform', `translate3d(-${activeIndex * 100}%, 0, 0)`, 'important');

      mainSlides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.dataset.isActive = String(active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.tabIndex = active ? 0 : -1;
      });
      thumbnailSlides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.dataset.isActive = String(active);
        slide.setAttribute('aria-current', active ? 'true' : 'false');
        slide.tabIndex = 0;
      });
      if (status) status.textContent = `Slide ${activeIndex + 1} of ${mainSlides.length}`;
      previous?.toggleAttribute('disabled', mainSlides.length < 2);
      next?.toggleAttribute('disabled', mainSlides.length < 2);
    };

    previous?.addEventListener('click', (event) => {
      event.preventDefault();
      setActive(activeIndex - 1);
    });
    next?.addEventListener('click', (event) => {
      event.preventDefault();
      setActive(activeIndex + 1);
    });
    thumbnailSlides.forEach((slide, index) => {
      slide.setAttribute('role', 'button');
      slide.setAttribute('aria-label', `Show product image ${index + 1}`);
      slide.addEventListener('click', () => setActive(index));
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setActive(index);
      });
    });
    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') setActive(activeIndex - 1);
      if (event.key === 'ArrowRight') setActive(activeIndex + 1);
    });
    carousel.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });
    carousel.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const distance = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      touchStartX = null;
      if (Math.abs(distance) >= 40) setActive(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });

    setActive(0, false);
  };

  const initializeCollapsibles = (root) => {
    root.querySelectorAll('[data-replo-collapsible="true"]').forEach((collapsible) => {
      if (collapsible.dataset.hrSpiritListicleBound === 'true') return;
      const trigger = collapsible.querySelector('[role="button"][aria-controls]');
      const contentId = trigger?.getAttribute('aria-controls');
      const content = contentId ? root.querySelector(`#${CSS.escape(contentId)}`) : null;
      if (!trigger || !content) return;
      collapsible.dataset.hrSpiritListicleBound = 'true';

      const setOpen = (open) => {
        collapsible.dataset.state = open ? 'open' : 'closed';
        trigger.dataset.state = open ? 'open' : 'closed';
        trigger.setAttribute('aria-expanded', String(open));
        content.hidden = !open;
      };
      const toggle = () => setOpen(trigger.getAttribute('aria-expanded') !== 'true');
      trigger.addEventListener('click', toggle);
      bindKeyboardActivation(trigger, toggle);
      setOpen(trigger.getAttribute('aria-expanded') === 'true');
    });
  };

  const initializeLinks = (root, config) => {
    const purchaseAnchorId = root.dataset.pageKey === 'list1' && root.querySelector('[data-hr-list1-campaign-buybox]')
      ? 'Buy'
      : PRODUCT_ANCHOR_ID;
    const scrollToTarget = (target) => {
      if (!target) return;
      if (root.dataset.pageKey === 'list1' && target.id === 'Buy') {
        window.scrollTo({
          top: Math.max(0, target.getBoundingClientRect().top + window.scrollY),
          behavior: 'smooth'
        });
        return;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const scrollToPurchase = (event) => {
      event?.preventDefault?.();
      scrollToTarget(document.getElementById(purchaseAnchorId));
    };

    (config.purchaseCtaRids || []).forEach((rid) => {
      const element = byRid(root, rid);
      element?.addEventListener('click', scrollToPurchase);
      bindKeyboardActivation(element, scrollToPurchase);
    });

    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = decodeURIComponent(link.getAttribute('href').slice(1));
        const target = id ? document.getElementById(id) : null;
        if (!target) return;
        event.preventDefault();
        scrollToTarget(target);
      });
    });

    const backToTop = byRid(root, config.backToTopRid);
    backToTop?.addEventListener('click', scrollToPurchase);
    bindKeyboardActivation(backToTop, scrollToPurchase);

    root.querySelectorAll('.jdgm-prev-badge, .jdgm-preview-badge').forEach((badge) => {
      badge.setAttribute('role', 'link');
      badge.tabIndex = 0;
      const goToReviews = () => document.getElementById('judgeme_product_reviews')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      badge.addEventListener('click', goToReviews);
      bindKeyboardActivation(badge, goToReviews);
    });
  };

  const initializeProduct = (root, config) => {
    const variants = config.variants || [];
    const optionNodes = (config.optionRids || []).map((rid) => byRid(root, rid)).filter(Boolean);
    const atcButtons = (config.atcRids || []).map((rid) => byRid(root, rid)).filter(Boolean);
    const form = root.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]');
    const sellingPlanInput = form?.querySelector('[name="selling_plan"]');
    if (!variants.length || optionNodes.length !== variants.length || atcButtons.length !== variants.length) return;

    const requestedVariantId = Number(new URL(window.location.href).searchParams.get('variant'));
    let selectedIndex = variants.findIndex((variant) => variant.id === requestedVariantId);
    if (selectedIndex < 0) selectedIndex = variants.findIndex((variant) => variant.id === config.defaultVariantId);
    if (selectedIndex < 0) selectedIndex = 0;
    let loading = false;

    const setButtonLabel = (button, label) => {
      button.querySelectorAll('p').forEach((node, index) => {
        if (index === 0) node.textContent = label;
        node.hidden = index > 0;
        node.setAttribute('aria-hidden', String(index > 0));
      });
    };

    const ensureLoadingIndicator = (button) => {
      let indicator = button.querySelector('.hr-spirit-listicle-loading');
      if (indicator) return indicator;
      indicator = document.createElement('span');
      indicator.className = 'hr-spirit-listicle-loading';
      indicator.setAttribute('aria-hidden', 'true');
      indicator.innerHTML = '<span>LOADING</span><span class="hr-spirit-listicle-spinner"></span>';
      button.appendChild(indicator);
      return indicator;
    };

    const render = () => {
      const variant = variants[selectedIndex];
      optionNodes.forEach((option, index) => {
        const selected = index === selectedIndex;
        option.classList.toggle(config.selectedClasses[index], selected);
        option.setAttribute('aria-selected', String(selected));
        option.setAttribute('aria-label', `${variants[index].title}${variants[index].available ? '' : ', sold out'}`);
        option.dataset.hrAvailable = String(Boolean(variants[index].available));
        option.tabIndex = selected ? 0 : -1;
      });

      atcButtons.forEach((button, index) => {
        const selected = index === selectedIndex;
        button.hidden = !selected;
        if (selected) button.style.setProperty('display', 'flex', 'important');
        else button.style.removeProperty('display');
        button.disabled = !variants[index].available || loading;
        button.setAttribute('aria-disabled', String(!variants[index].available || loading));
        button.dataset.hrLoading = String(selected && loading);
        button.setAttribute('aria-busy', String(selected && loading));
        setButtonLabel(button, variants[index].available ? config.addToCartLabel : config.soldOutLabel);
      });

      if (variantInput) variantInput.value = String(variant.id);
      if (quantityInput) quantityInput.value = '1';
      if (sellingPlanInput) sellingPlanInput.value = '';
    };

    const selectVariant = (index) => {
      if (!variants[index] || loading) return;
      selectedIndex = index;
      render();
    };

    optionNodes.forEach((option, index) => {
      option.addEventListener('click', () => selectVariant(index));
      option.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectVariant(index);
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          optionNodes[(index + 1) % optionNodes.length].focus();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          optionNodes[(index + optionNodes.length - 1) % optionNodes.length].focus();
        }
      });
    });

    const openCart = async (state) => {
      for (let attempt = 0; attempt < 600; attempt += 1) {
        if (typeof window.EliteCart?.openDrawerWithDataRefresh === 'function') {
          await window.EliteCart.openDrawerWithDataRefresh();
          return true;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.renderContents && state.sections) {
        cartDrawer.renderContents(state);
        return true;
      }
      return false;
    };

    const addToCart = async (event) => {
      event?.preventDefault?.();
      const variant = variants[selectedIndex];
      if (loading || !variant?.available) return;
      loading = true;
      const activeButton = atcButtons[selectedIndex];
      ensureLoadingIndicator(activeButton);
      render();

      const existingError = root.querySelector('.hr-spirit-listicle-error');
      existingError?.remove();
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', '1');
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);

      try {
        const response = await fetch(config.routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to add this item.');

        document.dispatchEvent(new CustomEvent('high-rider:cart-added', {
          detail: { productId: config.productId, variantId: variant.id, cartData: state }
        }));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'hr-spirit-listicle-owned', cartData: state, variantId: variant.id
          });
        }
        if (!await openCart(state)) window.location.assign(config.routes.cart || '/cart');
      } catch (error) {
        console.error('High Rider: Spirit listicle add to cart failed.', error);
        const errorNode = document.createElement('p');
        errorNode.className = 'hr-spirit-listicle-error';
        errorNode.setAttribute('role', 'alert');
        errorNode.textContent = error?.message || 'Unable to add this item. Please try again.';
        activeButton.insertAdjacentElement('afterend', errorNode);
      } finally {
        loading = false;
        render();
      }
    };

    atcButtons.forEach((button) => button.addEventListener('click', addToCart));
    form?.addEventListener('submit', addToCart);
    render();
  };

  const initialize = (root) => {
    if (root.dataset.hrSpiritListicleInitialized === 'true') return;
    const config = parseConfig(root);
    if (!config) return;
    root.dataset.hrSpiritListicleInitialized = 'true';
    initializeCarousel(root, config.gallery);
    initializeCollapsibles(root);
    initializeLinks(root, config);
    initializeProduct(root, config);
    window.jdgm?.initializeWidgets?.();
  };

  const initializeAll = (container = document) => {
    if (container.matches?.(ROOT_SELECTOR)) initialize(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
